/**
 * Shared state for the "What's New since your last visit" briefing.
 *
 * Why a store rather than hook-local state: the briefing is now generated in the
 * background from app root, while the Welcome tab renders it. Two components need
 * the same result, and hook-local state would give each its own copy — and its own
 * generation, so a user landing on the What's New tab would trigger a second LLM
 * call on top of the prefetch.
 *
 * `ensure` is single-flight and idempotent per user, so it's safe to call from as
 * many mount points as convenient.
 */

import { create } from "zustand";
import { getValidToken } from "../auth/tokenManager";
import {
  isSummaryDue,
  readUserInsightState,
  writeUserInsightState,
  USER_INSIGHT_SCHEMA_VERSION,
} from "../services/UserInsightStore";
import type {
  StoredInsightSummary,
  UserInsightState,
} from "../services/UserInsightStore";
import {
  generateWhatsNewSummary,
  type WhatsNewMachine,
  type WhatsNewVisit,
} from "../services/WhatsNewService";
import { isDemoMode } from "../services/demoMode";

export type BriefingStatus =
  | "idle"
  | "loading"
  | "generating"
  | "ready"
  | "empty"
  | "error";

/** Window used the very first time a user gets a briefing. */
const FIRST_VISIT_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

export interface BriefingInput {
  /** Stable per-user key (the token's `sub`) used for the KV entry. */
  userKey: string;
  role: string | null;
  visited: WhatsNewVisit[];
  park: WhatsNewMachine[];
  language: string;
}

interface WhatsNewState {
  status: BriefingStatus;
  summary: StoredInsightSummary | null;
  generatedAt: Date | null;
  windowStart: Date | null;
  error: string | null;
  /** Which user the current state belongs to, so a re-login doesn't reuse it. */
  userKey: string | null;
  inFlight: boolean;

  /**
   * Load today's briefing, generating one if due.
   *
   * @param force bypass the once-per-day gate (the Refresh button)
   */
  ensure: (input: BriefingInput, force: boolean) => Promise<void>;
  /** No machines available for this user — nothing to brief on. */
  markEmpty: (userKey: string) => void;
}

export const useWhatsNewStore = create<WhatsNewState>((set, get) => ({
  status: "idle",
  summary: null,
  generatedAt: null,
  windowStart: null,
  error: null,
  userKey: null,
  inFlight: false,

  markEmpty: (userKey) => {
    if (get().inFlight) return;
    set({ status: "empty", userKey });
  },

  ensure: async (input, force) => {
    // Demo mode: no LLM call, and no read or write of the per-user insight
    // state. A terminal status is essential — leaving it "idle" would leave the
    // Welcome tab on its loading skeleton forever.
    if (isDemoMode()) {
      set({ status: "empty", userKey: input.userKey, inFlight: false });
      return;
    }

    const state = get();

    // Single-flight: a second caller while one is running would duplicate the
    // LLM call, and the loser's write would race the winner's.
    if (state.inFlight) return;

    // Already loaded for this user in this session, and not a forced refresh.
    if (
      !force &&
      state.userKey === input.userKey &&
      (state.status === "ready" || state.status === "empty")
    ) {
      return;
    }

    set({
      inFlight: true,
      userKey: input.userKey,
      status: "loading",
      error: null,
    });

    try {
      const stored = await readUserInsightState(input.userKey);

      if (stored && !force && !isSummaryDue(stored)) {
        // Already briefed today — render the stored card. This is what makes a
        // page refresh free.
        set({
          summary: stored.summary,
          generatedAt: new Date(stored.generatedAt),
          windowStart: new Date(stored.windowStart),
          status: "ready",
          inFlight: false,
        });
        return;
      }

      // Show the previous briefing straight away rather than a blank card while
      // the new one is generated.
      if (stored) {
        set({
          summary: stored.summary,
          generatedAt: new Date(stored.generatedAt),
          windowStart: new Date(stored.windowStart),
        });
      }

      set({ status: "generating" });

      const now = new Date();

      // Which period to report on.
      //
      // Normally the window opens where the last briefing was generated, so each
      // day covers the time since the previous one. A *forced* refresh is
      // different: the user wants a better rendering of the period they're
      // already looking at, not a new one. Advancing to `generatedAt` there would
      // collapse the window to minutes, the agent would correctly find nothing in
      // it, and the card would come back empty — looking exactly like "nothing
      // changed".
      const from = (() => {
        if (force && stored?.windowStart) return new Date(stored.windowStart);
        if (stored?.generatedAt) return new Date(stored.generatedAt);
        return new Date(now.getTime() - FIRST_VISIT_WINDOW_MS);
      })();

      const token = await getValidToken();
      if (!token) throw new Error("No valid token available");

      const generated = await generateWhatsNewSummary(token, {
        role: input.role,
        windowStart: from,
        now,
        visited: input.visited,
        park: input.park,
        language: input.language,
      });

      const next: UserInsightState = {
        version: USER_INSIGHT_SCHEMA_VERSION,
        generatedAt: now.toISOString(),
        windowStart: from.toISOString(),
        summary: generated,
      };

      await writeUserInsightState(input.userKey, next);

      set({
        summary: generated,
        generatedAt: now,
        windowStart: from,
        status: "ready",
        inFlight: false,
      });
    } catch (e) {
      console.warn("What's New briefing failed:", e);
      set({
        error: e instanceof Error ? e.message : "Failed to build briefing",
        // A stale briefing beats an error card, so keep showing one if we have it.
        status: get().summary ? "ready" : "error",
        inFlight: false,
      });
    }
  },
}));
